/**
 * @module SESSION_HISTORY
 * @description Keeps the analyses the user has saved, so reopening one is a click rather than a
 * hunt through the downloads folder.
 * Stored in IndexedDB rather than in `localStorage`, which is where this app keeps everything else
 * it remembers: a session file runs from tens of kilobytes to eight megabytes, and `localStorage`
 * caps out around five for the whole origin - the first bathymetry session saved would have taken
 * the quota with it. IndexedDB also stores the object as it is, so nothing is serialized twice.
 */

const DATABASE_NAME = 'cassie';
const DATABASE_VERSION = 1;
const STORE_NAME = 'sessionHistory';

/**
 * @constant HISTORY_LIMIT
 * @description How many analyses are kept. Old ones fall off the end rather than the list growing
 * without bound - the point is to find the last few, and a session can be megabytes.
 * @memberof module:SESSION_HISTORY
 */
export const HISTORY_LIMIT = 20;

/**
 * @function openDatabase
 * @description Opens the database, creating the store on first use.
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<IDBDatabase|null>} The database, or null where IndexedDB is unavailable - a
 *  private window with storage blocked, for instance - so the history simply does not appear
 *  instead of breaking the page it sits in.
 */
const openDatabase = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || !window.indexedDB) return resolve(null);
    let request;
    try {
      request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (error) {
      console.warn('[SessionHistory] IndexedDB could not be opened:', error);
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('savedAt', 'savedAt');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn('[SessionHistory] IndexedDB could not be opened:', request.error);
      resolve(null);
    };
    return undefined;
  });

/**
 * @function withStore
 * @description Runs one transaction against the history store.
 * @param {('readonly'|'readwrite')} mode - The transaction mode.
 * @param {Function} work - Receives the store and returns the request to wait on.
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<*>} What the request produced, or null when the store is unavailable.
 */
const withStore = async (mode, work) => {
  const database = await openDatabase();
  if (!database) return null;
  return new Promise((resolve) => {
    let transaction;
    try {
      transaction = database.transaction(STORE_NAME, mode);
    } catch (error) {
      console.warn('[SessionHistory] the history could not be read:', error);
      return resolve(null);
    }
    const request = work(transaction.objectStore(STORE_NAME));
    transaction.oncomplete = () => database.close();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn('[SessionHistory] the history could not be written:', request.error);
      resolve(null);
    };
    return undefined;
  });
};

/**
 * @function listSessions
 * @description Every analysis in the history, newest first, without their contents - the list only
 * needs what it shows, and a session is too big to load twenty of at once.
 * **Called by:
 * @see {SessionHistoryDialog}
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<Array<Object>>} `{id, module, title, detail, savedAt}` entries.
 */
export const listSessions = async () => {
  const entries = await withStore('readonly', (store) => store.getAll());
  if (!entries) return [];
  return entries
    .map(({ session, ...summary }) => summary)
    .sort((one, other) => other.savedAt - one.savedAt);
};

/**
 * @function readSession
 * @description The saved analysis itself, ready to be handed to its module's importer.
 * @param {String} id - The entry's id.
 * **Called by:
 * @see {SessionHistoryDialog} - when the user reopens one.
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<Object|null>} The session, or null when it is no longer there.
 */
export const readSession = async (id) => {
  const entry = await withStore('readonly', (store) => store.get(id));
  return entry?.session || null;
};

/**
 * @function deleteSession
 * @description Removes one analysis from the history. The downloaded file, if there is one, is the
 * user's own and is untouched.
 * @param {String} id - The entry's id.
 * **Called by:
 * @see {SessionHistoryDialog}
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<void>}
 */
export const deleteSession = async (id) => {
  await withStore('readwrite', (store) => store.delete(id));
};

/**
 * @function trimHistory
 * @description Drops the oldest entries past {@link HISTORY_LIMIT}.
 * **Called by:
 * @see {saveSession}
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<void>}
 */
const trimHistory = async () => {
  const entries = await listSessions();
  const extra = entries.slice(HISTORY_LIMIT);
  for (const entry of extra) {
    await deleteSession(entry.id);
  }
};

/**
 * @function saveSession
 * @description Puts an analysis in the history, under what it is about.
 * The same analysis saved twice replaces its own entry rather than piling up: the id is the module
 * and what the analysis covers, which is what the user would call it anyway.
 * @param {Object} session - The snapshot, as `buildSessionExport` returns it.
 * @param {Object} summary - `{title, detail}`, as {@link sessionSummary} describes it.
 * **Called by:
 * @see {exportSession} - saving the file also files it here.
 * @see {SessionImportButton} - so a file opened from elsewhere joins the history.
 * @memberof module:SESSION_HISTORY
 * @returns {Promise<Boolean>} Whether it was stored.
 */
export const saveSession = async (session, summary) => {
  if (!session?.module) return false;
  const id = `${session.module}-${summary?.title || ''}-${summary?.detail || ''}`.toLowerCase();
  const stored = await withStore('readwrite', (store) =>
    store.put({
      id,
      module: session.module,
      title: summary?.title || session.module,
      detail: summary?.detail || '',
      savedAt: Date.now(),
      session,
    }),
  );
  if (stored === null) return false;
  await trimHistory();
  return true;
};
